import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Warning as WarningIcon,
  AccessTime as ClockIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  TrendingFlat as TrendingFlatIcon,
} from '@mui/icons-material';
import { LocationStatus, PourSummary } from '../../services/pours';
import {
  isLoggingEnded,
  locationOnlineStatus,
  locationStartsAt,
  type OnlineStatus,
} from '../../utils/onlineStatus';
import { StatusBadge } from './StatusBadge';
import { MaturityProgress, type MaturityProgressDetail } from './MaturityProgress';
import {
  TemperatureProfileBar,
  DEFAULT_SCALE_MAX,
  type TempLimitMarker,
} from './TemperatureProfileBar';
import { formatDateTimeWithZone, type IsoLike } from '../../utils/datetime';

import {
  isHumidityLocation,
  isSiteConditionsChannel,
  orderByFunction,
} from '../../utils/locationChannels';
import { locationHasMaturity } from '../../utils/locationMaturity';
import {
  formatMaturity,
  formatMaturityWithIndex,
  formatStrengthReading,
  maturityMeta,
  maturityShortTitle,
  pourHasCurve,
  resolveStrengthReading,
  resolveStrengthUnit,
  strengthReadingTooltip,
} from '../../utils/maturityDisplay';
import { OutsideRangeTargets, StrengthTip, tipUnderline } from './StrengthDisplay';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format a channel value with its unit (°C, or %RH for humidity channels). */
const formatChannelValue = (l: LocationStatus, v?: number | null): string =>
  v != null ? `${v.toFixed(1)} ${isHumidityLocation(l) ? '%' : '°C'}` : '-';

/** Simple 0-100% fill bar for humidity channels (the temperature profile
 *  gradient is meaningless for relative humidity). */
const HumidityBar: React.FC<{ value?: number | null; height?: number }> = ({
  value,
  height = 28,
}) => (
  <Box
    sx={{
      position: 'relative',
      height,
      borderRadius: 1,
      backgroundColor: 'grey.200',
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: `${Math.min(Math.max(value ?? 0, 0), 100)}%`,
        backgroundColor: '#4a90c2',
        transition: 'width 0.3s',
      }}
    />
    <Typography
      variant="caption"
      sx={{
        position: 'absolute',
        right: 6,
        top: '50%',
        transform: 'translateY(-50%)',
        fontWeight: 600,
        color: 'text.secondary',
      }}
    >
      {value != null ? `${value.toFixed(1)} %RH` : '-'}
    </Typography>
  </Box>
);

/** Choose a trending icon based on current vs. max temperature. */
const TrendIcon: React.FC<{ current?: number; max?: number }> = ({
  current,
  max,
}) => {
  if (current == null || max == null) return <TrendingFlatIcon fontSize="small" color="disabled" />;
  const ratio = max > 0 ? current / max : 1;
  // Near peak (>95%) → still rising / at peak
  // Cooling but near peak (>85%) → levelling off
  // Well below peak (≤85%) → clearly cooling
  if (ratio > 0.95) return <TrendingUpIcon fontSize="small" color="error" />;
  if (ratio > 0.85) return <TrendingFlatIcon fontSize="small" color="warning" />;
  return <TrendingDownIcon fontSize="small" color="success" />;
};

/** Muted text colour per freshness level, so a stalled logger reads at a
 *  glance without shouting. */
const LAST_READ_COLORS: Record<OnlineStatus['level'], string> = {
  online: '#2e7d32',
  delayed: '#9a6a00',
  offline: '#A32D2D',
  ended: '#757575',
  scheduled: '#185FA5',
};

/** "Last read" line with a small clock icon, coloured by logger freshness.
 *  A location waiting for its start says when it starts instead, one that
 *  started but has no reading yet says when it started, one Offline without
 *  any reading says so plainly, and one whose logging ended says so in grey
 *  rather than a red "Offline". */
const LastReadIndicator: React.FC<{
  status: OnlineStatus;
  lastRead?: string;
  startsAt?: IsoLike;
  format: (d?: IsoLike) => string;
}> = ({ status, lastRead, startsAt, format }) => {
  const color = LAST_READ_COLORS[status.level];
  const detail =
    status.level === 'scheduled'
      ? `Starts ${format(startsAt)}`
      : status.awaiting
        ? startsAt != null ? `Started ${format(startsAt)}` : null
        : !lastRead
          ? status.level === 'ended'
            ? null
            : 'No readings received'
          : `Last read ${format(lastRead)}`;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <ClockIcon sx={{ fontSize: 13, color, flexShrink: 0 }} />
      <Typography variant="caption" sx={{ color, fontSize: '0.7rem' }}>
        {status.label}
        {detail ? ` · ${detail}` : ''}
      </Typography>
    </Box>
  );
};

/** Map status code to a warning icon when applicable. */
const StatusIcon: React.FC<{ status: string }> = ({ status }) => {
  if (status === 'good') return null;
  return (
    <WarningIcon
      fontSize="small"
      sx={{
        color: status === 'error' ? 'error.main' : 'warning.main',
        ml: 0.5,
        verticalAlign: 'middle',
      }}
    />
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface LocationListProps {
  locations: LocationStatus[];
  pour?: PourSummary;
  className?: string;
  /** Called when a row (desktop) or card (mobile) is clicked.  Dashboard uses
   *  this to jump to the Location Details tab with the selected location. */
  onSelectLocation?: (locationId: string) => void;
  /** Temperature limits to reference on each profile bar (Criteria '02'). */
  limits?: TempLimitMarker[];
  scaleMax?: number;
}

export const LocationList: React.FC<LocationListProps> = ({
  locations,
  pour,
  className = '',
  onSelectLocation,
  limits,
  scaleMax = DEFAULT_SCALE_MAX,
}) => {
  const theme = useTheme();
  // Cards below md, the same breakpoint the dashboard page itself switches at.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [viewMode, setViewMode] = useState<'temperature' | 'progress'>(
    'temperature',
  );

  // Targets only count on locations that accrue maturity.
  const hasTargets = locations.some(
    (l) => locationHasMaturity(l) && l.targets && l.targets.length > 0,
  );
  // The toggle only swaps the bar on maturity rows. Site-conditions channels
  // (ambient Temp & Humidity, weather) and Temperature Only locations have
  // no maturity or strength, so they keep their temperature / %RH bar in
  // either mode instead of switching to an empty progress bar.
  const rowViewMode = (l: LocationStatus): 'temperature' | 'progress' =>
    locationHasMaturity(l) ? viewMode : 'temperature';
  // Freshness per location: the bands, or "Logging ended" on a completed
  // pour / finished location, or "Scheduled" before its start. A weather
  // station follows the same rule on its newest weather reading.
  const onlineOf = (l: LocationStatus): OnlineStatus => locationOnlineStatus(l, pour?.status);
  // Before the start and after logging ended an idle logger is expected:
  // no heartbeat warning or "Offline" pill then.
  const heartbeatHidden = (l: LocationStatus): boolean => {
    const level = onlineOf(l).level;
    return level === 'ended' || level === 'scheduled';
  };
  // What each target progress tooltip needs to say when a target was reached
  // (measured from the concrete start, as the Targets card) or how much
  // maturity is still to go, and to flag targets outside the tested range.
  const progressDetail = (l: LocationStatus): MaturityProgressDetail => ({
    currentMaturity: l.current_ea,
    concreteStart: l.ea_start,
    monitoringStart: l.logging_started,
    timezoneIana: pour?.timezone_iana,
    ended: isLoggingEnded(l, pour?.status),
    range: pour?.calibrated_range,
  });
  const hasHumidity = locations.some(isHumidityLocation);
  // Multi-function pours: rows grouped by monitoring function, every group
  // introduced by a quiet label (single-function pours get none).
  const { ordered: orderedLocations, groupLabels } = orderByFunction(locations);
  // The 0..scaleMax axis belongs under the last row that draws a temperature
  // bar — humidity rows render a %RH bar and can't host it.
  const lastTempBarIdx = orderedLocations.reduce(
    (last, l, idx) => (isHumidityLocation(l) ? last : idx),
    -1,
  );
  // Ambient bars skip the limit markers but keep the shared 0..scaleMax axis
  // (forceScale) whenever the pour actually has limits, so equal temperatures
  // line up across concrete and ambient rows.
  const hasLimitMarkers = (limits?.length ?? 0) > 0;
  // Strength is a maturity concept — hide the column entirely for pours whose
  // monitoring functions include neither maturity nor TMC (e.g. pure External
  // Temperature & Humidity pours). Legacy pours without the functions string
  // keep the column.
  const funcs = pour?.monitoring_functions;
  const showStrength =
    !funcs ||
    funcs.includes('Concrete Maturity') ||
    funcs.includes('Temperature Match Curing');
  // Maturity naming follows the pour's method (only "Nurse Saul" is TTF);
  // strength carries the company unit. Without a usable calibration curve
  // there is no strength at all, so the column shows maturity instead.
  const meta = maturityMeta(pour);
  const strengthUnit = resolveStrengthUnit(pour?.strength_unit);
  const maturityOnly = pourHasCurve(pour) === false;
  // Maturity-only: the heading names the index, its specified temperature
  // and unit ("EA at 20 °C (hrs)" / "TTF (°C·hrs)"), as the control panel.
  const strengthHeading = maturityOnly ? maturityShortTitle(meta) : 'Strength';
  const strengthOf = (l: LocationStatus) => {
    const reading = resolveStrengthReading(l.strength, l.current_strength);
    return { text: formatStrengthReading(reading, strengthUnit), tip: strengthReadingTooltip(reading) };
  };

  // Reference max for temperature profile — highest max_temp across all
  // TEMPERATURE locations (humidity channels carry %RH, not °C, and would
  // skew the shared temperature scale).
  const referenceMax = Math.max(
    ...locations.filter((l) => !isHumidityLocation(l)).map((l) => l.max_temp ?? 0),
    1, // avoid 0
  );

  // In the pour's timezone, named ("18 Oct 2026, 10:30 AEDT").
  const formatDate = (dateString?: IsoLike) => {
    const out = formatDateTimeWithZone(dateString, pour?.timezone_iana);
    return out || 'N/A';
  };

  // Short status label for the compact mobile pill.
  const getShortStatusText = (status: string) => {
    switch (status) {
      case 'good':
        return 'Good';
      case 'warning':
        return 'Warning';
      case 'error':
        return 'Offline';
      default:
        return status;
    }
  };

  // ------------------------------- Empty -----------------------------------
  if (!locations.length) {
    return (
      <Paper elevation={2} className={className} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Monitoring Locations
        </Typography>
        <Box display="flex" justifyContent="center" py={8}>
          <Typography color="text.secondary">
            No monitoring locations found for this pour.
          </Typography>
        </Box>
      </Paper>
    );
  }

  // ----------------------------- Styles ------------------------------------
  const headerSx = {
    fontWeight: 600,
    fontSize: '0.75rem',
    color: 'text.secondary',
    backgroundColor: 'grey.50',
    whiteSpace: 'nowrap',
    py: 1,
    px: 1,
  };

  /** Keep data columns tight so the bar gets the lion's share of width. */
  const compactCellSx = { px: 1, whiteSpace: 'nowrap' } as const;

  // ========================== DESKTOP VIEW =================================
  if (!isMobile) {
    return (
      <Paper elevation={2} className={className}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headerSx, width: '1%' }}>Location</TableCell>
                <TableCell sx={{ ...headerSx, width: '1%' }} align="center">
                  {hasHumidity ? 'Current' : 'Current Temp'}
                </TableCell>
                <TableCell sx={{ ...headerSx, width: '1%' }} align="center">
                  {hasHumidity ? 'Max' : 'Max Temp'}
                </TableCell>
                <TableCell sx={{ ...headerSx, width: '1%' }} align="center">
                  Trending
                </TableCell>
                {showStrength && (
                  <TableCell sx={{ ...headerSx, width: '1%' }} align="center">
                    {strengthHeading}
                  </TableCell>
                )}

                {/* Right section: toggle header + progress / temp profile */}
                <TableCell
                  sx={{
                    ...headerSx,
                    textAlign: 'left',
                    pl: 1,
                  }}
                >
                  {hasTargets ? (
                    <ToggleButtonGroup
                      value={viewMode}
                      exclusive
                      onChange={(_e, v) => v && setViewMode(v)}
                      size="small"
                      sx={{
                        '& .MuiToggleButton-root': {
                          textTransform: 'none',
                          px: 1.5,
                          py: 0.25,
                          fontSize: '0.75rem',
                          lineHeight: 1.4,
                        },
                      }}
                    >
                      <ToggleButton value="temperature">
                        Temperature Profile
                      </ToggleButton>
                      <ToggleButton value="progress">
                        Target Progress
                      </ToggleButton>
                    </ToggleButtonGroup>
                  ) : (
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      Temperature Profile
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {orderedLocations.map((location, idx) => (
                <React.Fragment key={location.id}>
                {/* Quiet group label above each monitoring-function group
                    (multi-function pours only). */}
                {groupLabels.has(idx) && (
                  <TableRow>
                    <TableCell
                      colSpan={showStrength ? 6 : 5}
                      sx={{ pt: 1.25, pb: 0.25, px: 1, border: 'none' }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontSize: '0.68rem', fontWeight: 600, color: 'text.disabled', letterSpacing: 0.3 }}
                      >
                        {groupLabels.get(idx)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                <TableRow
                  hover={!!onSelectLocation}
                  onClick={
                    onSelectLocation
                      ? () => onSelectLocation(location.id)
                      : undefined
                  }
                  sx={{
                    cursor: onSelectLocation ? 'pointer' : 'default',
                    '&:hover': { backgroundColor: 'action.hover' },
                    '& td': { py: 1.5 },
                  }}
                >
                  {/* ---- Location (compound column) ---- */}
                  <TableCell sx={compactCellSx}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      fontSize="0.9rem"
                      noWrap
                    >
                      {location.name || `Location ${location.id.slice(-8)}`}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mt: 0.25,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontSize="0.7rem"
                        noWrap
                      >
                        {location.logger_code}
                        {location.battery
                          ? ` (${location.battery}`
                          : ''}
                        {location.signal
                          ? ` / ${location.signal})`
                          : location.battery
                            ? ')'
                            : ''}
                      </Typography>
                      {/* A logger that is quiet before its start or after
                          its logging ended is expected, not a fault. */}
                      {!heartbeatHidden(location) && (
                        <StatusIcon status={location.status} />
                      )}
                    </Box>
                  </TableCell>

                  {/* ---- Current value (°C, or %RH for humidity channels) ---- */}
                  <TableCell align="center" sx={compactCellSx}>
                    <Typography variant="body1" fontWeight={600} fontSize="0.95rem">
                      {formatChannelValue(location, location.current_temp)}
                    </Typography>
                  </TableCell>

                  {/* ---- Max value ---- */}
                  <TableCell align="center" sx={compactCellSx}>
                    <Typography variant="body1" fontWeight={600} fontSize="0.95rem">
                      {formatChannelValue(location, location.max_temp)}
                    </Typography>
                  </TableCell>

                  {/* ---- Trending (peak-relative; not meaningful for RH) ---- */}
                  <TableCell align="center" sx={compactCellSx}>
                    {isHumidityLocation(location) ? (
                      <Typography variant="body1" color="text.secondary">
                        -
                      </Typography>
                    ) : (
                      <TrendIcon
                        current={location.current_temp}
                        max={location.max_temp}
                      />
                    )}
                  </TableCell>

                  {/* ---- Strength (Maturity; only on locations that accrue it) ---- */}
                  {showStrength && (
                    <TableCell align="center" sx={compactCellSx}>
                      {!locationHasMaturity(location) ? (
                        <Typography variant="body1" color="text.secondary">
                          -
                        </Typography>
                      ) : maturityOnly ? (
                        <Typography variant="body1" fontWeight={700} fontSize="0.95rem" noWrap>
                          {formatMaturity(location.current_ea, meta)}
                        </Typography>
                      ) : (() => {
                        const s = strengthOf(location);
                        return (
                          <>
                            <StrengthTip title={s.tip}>
                              <Typography
                                variant="body1"
                                fontWeight={700}
                                fontSize="0.95rem"
                                noWrap
                                color={s.text === '-' ? 'text.secondary' : undefined}
                                sx={tipUnderline(s.tip)}
                              >
                                {s.text}
                              </Typography>
                            </StrengthTip>
                            {location.current_ea != null && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                fontSize="0.7rem"
                              >
                                ({formatMaturityWithIndex(location.current_ea, meta)})
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                    </TableCell>
                  )}

                  {/* ---- Temperature Profile / Target Progress ---- */}
                  <TableCell>
                    {/* Last Read — above the bar */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 0.5,
                        mb: 0.5,
                      }}
                    >
                      <LastReadIndicator
                        status={onlineOf(location)}
                        lastRead={location.last_read}
                        startsAt={locationStartsAt(location)}
                        format={formatDate}
                      />
                    </Box>

                    {/* Bar */}
                    {rowViewMode(location) === 'temperature' ? (
                      isHumidityLocation(location) ? (
                        <HumidityBar value={location.current_temp} height={28} />
                      ) : (
                      <TemperatureProfileBar
                        maxMarker
                        flat={isSiteConditionsChannel(location)}
                        currentTemp={location.current_temp}
                        maxTemp={location.max_temp}
                        referenceMax={referenceMax}
                        // Site-conditions channels (ambient T&H, weather) have
                        // no concrete limits — hide the markers but keep the
                        // shared axis scale.
                        limits={isSiteConditionsChannel(location) ? undefined : limits}
                        forceScale={isSiteConditionsChannel(location) && hasLimitMarkers}
                        scaleMax={scaleMax}
                        showScale={idx === lastTempBarIdx}
                        sx={{ height: 28 }}
                      />
                      )
                    ) : (
                      <>
                        <MaturityProgress
                          targets={location.targets}
                          strengthUnit={strengthUnit}
                          maturity={meta}
                          detail={progressDetail(location)}
                          sx={{ height: 28 }}
                        />
                        <OutsideRangeTargets
                          targets={location.targets}
                          range={pour?.calibrated_range}
                          strengthUnit={strengthUnit}
                        />
                      </>
                    )}
                  </TableCell>
                </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  // ========================== MOBILE VIEW ==================================
  return (
    <Box className={className}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          Monitoring locations ({locations.length})
        </Typography>
        {hasTargets && (
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_e, v) => v && setViewMode(v)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                px: 1,
                py: 0.25,
                fontSize: '0.7rem',
              },
            }}
          >
            <ToggleButton value="temperature">Temp</ToggleButton>
            <ToggleButton value="progress">Targets</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {orderedLocations.map((location, idx) => (
          <React.Fragment key={location.id}>
          {/* Quiet group label above each monitoring-function group
              (multi-function pours only). */}
          {groupLabels.has(idx) && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.68rem',
                fontWeight: 600,
                color: 'text.disabled',
                letterSpacing: 0.3,
                mt: idx === 0 ? 0 : 0.5,
                mb: -0.75,
              }}
            >
              {groupLabels.get(idx)}
            </Typography>
          )}
          <Card
            elevation={0}
            onClick={
              onSelectLocation
                ? () => onSelectLocation(location.id)
                : undefined
            }
            sx={{
              border: '0.5px solid',
              borderColor: 'divider',
              borderRadius: 2,
              cursor: onSelectLocation ? 'pointer' : 'default',
            }}
          >
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              {/* Header: name + status */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                gap={1}
                mb={1.25}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body1" fontWeight={600} noWrap>
                    {location.name || `Location ${location.id.slice(-8)}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {location.logger_code}
                    {location.battery ? ` (${location.battery}` : ''}
                    {location.signal
                      ? ` / ${location.signal})`
                      : location.battery
                        ? ')'
                        : ''}
                  </Typography>
                </Box>
                {/* Grey "Scheduled" before the start and "Logging ended"
                    once the location has stopped logging, rather than the
                    heartbeat's red "Offline" for a logger that is idle. */}
                {heartbeatHidden(location) ? (
                  <StatusBadge status="unknown" size="sm">
                    {onlineOf(location).label}
                  </StatusBadge>
                ) : (
                  <StatusBadge status={location.status as any} size="sm">
                    {getShortStatusText(location.status)}
                  </StatusBadge>
                )}
              </Box>

              {/* Stats grid */}
              <Box
                display="grid"
                gridTemplateColumns="1fr 1fr 1fr"
                gap={1}
                mb={1.5}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {isHumidityLocation(location) ? 'Current Humidity' : 'Current Temp'}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatChannelValue(location, location.current_temp)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {isHumidityLocation(location) ? 'Max Humidity' : 'Max Temp'}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatChannelValue(location, location.max_temp)}
                  </Typography>
                </Box>
                {showStrength && (() => {
                  const noMaturity = !locationHasMaturity(location);
                  const s = noMaturity || maturityOnly ? null : strengthOf(location);
                  return (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {strengthHeading}
                      </Typography>
                      <StrengthTip title={s?.tip}>
                        <Typography variant="body2" fontWeight={500} sx={tipUnderline(s?.tip)}>
                          {noMaturity
                            ? '-'
                            : s
                              ? s.text
                              : formatMaturity(location.current_ea, meta)}
                        </Typography>
                      </StrengthTip>
                      {s && location.current_ea != null && (
                        <Typography variant="caption" color="text.disabled">
                          ({formatMaturityWithIndex(location.current_ea, meta)})
                        </Typography>
                      )}
                    </Box>
                  );
                })()}
              </Box>

              {/* Temperature Profile / Target Progress bar */}
              <Box mt={0.5}>
                {rowViewMode(location) === 'temperature' ? (
                  isHumidityLocation(location) ? (
                    <HumidityBar value={location.current_temp} height={14} />
                  ) : (
                  <TemperatureProfileBar
                    simple
                    maxMarker
                    flat={isSiteConditionsChannel(location)}
                    currentTemp={location.current_temp}
                    maxTemp={location.max_temp}
                    referenceMax={referenceMax}
                    // Site-conditions channels (ambient T&H, weather) have no
                    // concrete limits — hide the markers but keep the shared
                    // axis scale.
                    limits={isSiteConditionsChannel(location) ? undefined : limits}
                    forceScale={isSiteConditionsChannel(location) && hasLimitMarkers}
                    scaleMax={scaleMax}
                    sx={{ height: 14 }}
                  />
                  )
                ) : (
                  <>
                    <MaturityProgress
                      targets={location.targets}
                      strengthUnit={strengthUnit}
                      maturity={meta}
                      detail={progressDetail(location)}
                      sx={{ height: 16 }}
                    />
                    <OutsideRangeTargets
                      targets={location.targets}
                      range={pour?.calibrated_range}
                      strengthUnit={strengthUnit}
                    />
                  </>
                )}
              </Box>

              {/* Last read */}
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                mt={1}
                justifyContent="flex-start"
                flexWrap="wrap"
              >
                <LastReadIndicator
                  status={onlineOf(location)}
                  lastRead={location.last_read}
                  startsAt={locationStartsAt(location)}
                  format={formatDate}
                />
              </Box>
            </CardContent>
          </Card>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};
